import { useEffect, useRef, useState } from 'react';
import { loadMessageMedia, openMessageMedia } from '../services/mediaService';

/**
 * Adjuntos de un mensaje pintados desde waserver con la sesión del agente
 * (ver services/mediaService.js), no desde la URL pública del bucket.
 *
 * Respaldo: si el servidor no puede dar el adjunto (o el mensaje aún no tiene
 * id), se usa `msg.mediaFilename` como hasta ahora — solo si es una URL http(s).
 * Con la doble escritura apagada en waserver, `mediaFilename` es un `gs://` del
 * bucket privado, que un navegador no puede abrir.
 */

function originalUrl(msg) {
  return /^https?:\/\//i.test(msg.mediaFilename || '') ? msg.mediaFilename : undefined;
}

const placeholderStyle = {
  minWidth: '120px',
  minHeight: '90px',
  backgroundColor: '#f1f3f5',
};

/**
 * Baja el adjunto cuando el elemento entra en pantalla.
 * @returns {{ ref, src: string|undefined, loading: boolean }}
 */
function useMessageMedia(conversationId, msg) {
  const ref = useRef(null);
  const [state, setState] = useState({ url: null, failed: false });
  const messageId = msg.id;

  useEffect(() => {
    setState({ url: null, failed: false });
    if (!messageId || !conversationId) {
      setState({ url: null, failed: true });
      return undefined;
    }

    let cancelled = false;
    const start = () => {
      loadMessageMedia(conversationId, messageId)
        .then((media) => {
          if (!cancelled) setState({ url: media.url, failed: false });
        })
        .catch((err) => {
          console.warn('[MessageMedia] adjunto no disponible por el servidor, se usa la URL original:', err.message);
          if (!cancelled) setState({ url: null, failed: true });
        });
    };

    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      start();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          start();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [conversationId, messageId]);

  const src = state.url || (state.failed ? originalUrl(msg) : undefined);
  return { ref, src, loading: !src };
}

function openOriginal(msg) {
  const url = originalUrl(msg);
  if (url) window.open(url, '_blank', 'noopener');
}

async function openAttachment(conversationId, msg, filename) {
  if (!msg.id) {
    openOriginal(msg);
    return;
  }
  try {
    await openMessageMedia(conversationId, msg.id, filename);
  } catch (err) {
    console.warn('[MessageMedia] adjunto no disponible por el servidor, se abre la URL original:', err.message);
    openOriginal(msg);
  }
}

export function MessageImage({ conversationId, msg, alt, style, clickable = true }) {
  const { ref, src, loading } = useMessageMedia(conversationId, msg);
  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      style={{ ...style, ...(loading ? placeholderStyle : null) }}
      onClick={clickable ? () => openAttachment(conversationId, msg, msg.mediaMetadata?.filename) : undefined}
    />
  );
}

export function MessageVideo({ conversationId, msg, style, children }) {
  const { ref, src } = useMessageMedia(conversationId, msg);
  return (
    <video ref={ref} controls src={src} style={style}>
      {children}
    </video>
  );
}

export function MessageAudio({ conversationId, msg, style, children }) {
  const { ref, src } = useMessageMedia(conversationId, msg);
  return (
    <audio ref={ref} controls src={src} style={style}>
      {children}
    </audio>
  );
}

/**
 * Enlace a un documento: lo baja al hacer clic y lo abre (PDF, imagen) o lo
 * descarga (cualquier otro tipo).
 */
export function MessageDocumentLink({ conversationId, msg, filename, style, children, ...rest }) {
  const [busy, setBusy] = useState(false);

  const handleClick = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await openAttachment(conversationId, msg, filename || msg.mediaMetadata?.filename);
    } finally {
      setBusy(false);
    }
  };

  return (
    <a
      href="#adjunto"
      onClick={handleClick}
      style={{ ...style, cursor: busy ? 'progress' : 'pointer' }}
      {...rest}
    >
      {children}
    </a>
  );
}
